import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { AppEnv, env } from 'config' // Adjust the import path as necessary
import type { Client, Event } from '@bugsnag/js'

// Errors thrown by scripts we don't control and can't fix.
const IGNORED_MESSAGE_PATTERNS = [
  // Injected by the Facebook/Instagram in-app browser on iOS
  /_AutofillCallbackHandler/,
  // Benign per the ResizeObserver spec: the observer skipped a frame, nothing broke
  /ResizeObserver loop/,
  // Google reCAPTCHA's internal timeout, thrown from its own script
  /reCAPTCHA Timeout/
]

const THIRD_PARTY_SCRIPT_HOSTS = [
  // cspell:disable-next-line
  'www.googletagmanager.com',
  'maps.googleapis.com',
  // cspell:disable-next-line
  'www.gstatic.com/recaptcha'
]

// Frames without a resolvable source file: browser internals and inline scripts
const isNeutralFile = (file: string | undefined) =>
  !file || file === '[native code]' || file === 'global code'

const isThirdPartyFile = (file: string | undefined) =>
  file !== undefined && THIRD_PARTY_SCRIPT_HOSTS.some((host) => file.includes(host))

export const isIgnoredThirdPartyError = (event: Event) => {
  const [error] = event.errors

  if (!error) {
    return false
  }

  if (IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(error.errorMessage))) {
    return true
  }

  const stacktrace = error.stacktrace ?? []
  const hasThirdPartyFrame = stacktrace.some((frame) => isThirdPartyFile(frame.file))
  const hasAppFrame = stacktrace.some(
    (frame) => !isThirdPartyFile(frame.file) && !isNeutralFile(frame.file)
  )

  return hasThirdPartyFrame && !hasAppFrame
}

let bugsnagClient: Pick<Client, 'notify' | 'getUser' | 'setUser'> = {
  notify: (error: any) => {
    // eslint-disable-next-line no-console
    console.error(error)
  },
  getUser: () => ({}),
  setUser: () => {}
}

if (env.REACT_APP_ENV !== AppEnv.Development) {
  bugsnagClient = Bugsnag.start({
    apiKey: env.REACT_APP_BUGSNAG_API_KEY as string,
    plugins: [new BugsnagPluginReact()],
    releaseStage: env.REACT_APP_ENV,
    onError: (event) => !isIgnoredThirdPartyError(event)
  })
}

export default bugsnagClient
